import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Badge,
  Button,
  Form,
  InputGroup,
  OverlayTrigger,
  Popover,
  Table,
  Tooltip,
} from "react-bootstrap";
import DatePicker from "react-date-picker";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import { useFormik } from "formik";
import * as Yup from "yup";
import _ from "lodash";
import moment from "moment";
import { toast } from "react-toastify";
import "./recordusage.scss";
import CustomModal from "../../components/customModal/CustomModal";
import { toggleClass } from "../../utils/commonUtils";
import useRecordUsage from "../../Hooks/useRecordUsage";
import usePatient from "../../Hooks/usePatient";
import useAdmin from "../../Hooks/useAdmin";
import { useAppSelector } from "../../redux/hooks";
import useUnits from "../../Hooks/useUnits";
import useUser from "../../Hooks/useUser";
import useLocations from "../../Hooks/useLocations";
import Loading from "../../components/LoadingPage/Loading";
import { DropDownValues } from "../../utils/types";
import {
  Payload,
  Provider,
  RecordUsageRes,
} from "../../redux/recordUsage/recordUsageApiSlice";
import { clientId } from "../../redux/auth/authSlice";
import {
  checkPermission,
  PermissionIdentifier,
  PermissionModule,
} from "../../utils/permissionUtils";

// Validation schemas
const recordUsageValidationSchema = Yup.object({
  patID: Yup.string().required("Patient is required"),
  hcpid: Yup.string().required("Provider is required"),
  location: Yup.string().required("Location is required"),
  barcode: Yup.string()
    .required("Barcode is required")
    .max(75, "Barcode should not be more than 75 characters"),
  qty: Yup.number()
    .moreThan(0, "Quantity cannot be less than or equal to 0")
    .required("Quantity is required"),
  waste: Yup.number()
    .min(0, "Waste cannot be less than 0")
    .required("Waste is required"),
});

const patientValidationSchema = Yup.object({
  firstName: Yup.string().required("First Name is Required"),
  lastName: Yup.string().required("Last Name is Required"),
  patientCode: Yup.string().required("Patient ID is Required"),
  dob: Yup.date().nullable(),
  gender: Yup.string().required("Gender is required"),
});

interface RecordUsageState {
  locationItem: any[];
  recordUsagePageSetting: any;
  patients: any[];
  ddPatients: DropDownValues[];
  ddProviders: DropDownValues[];
  ddLocation: DropDownValues[];
  providers: Provider[];
  itemData?: RecordUsageRes;
  shotData: any[];
  useUnitsList: any[];
  isCurrentUserSet: boolean;
}

const initialRecordUsageState: RecordUsageState = {
  locationItem: [],
  recordUsagePageSetting: undefined,
  patients: [],
  ddPatients: [],
  ddProviders: [],
  ddLocation: [],
  providers: [],
  itemData: undefined,
  shotData: [],
  useUnitsList: [],
  isCurrentUserSet: false,
};

const RecordUsage: React.FC = () => {
  // Hooks
  const { getDDvaluesAPI, getBarcodeDetailsAPI, getDDvaluesLoading, getBarcodeDetailsLoading } = useRecordUsage();
  const { createPatientAPI, createPatientLoading, getPatientAPI, getPatientLoading } = usePatient();
  const { getTenantKeyAPI, getTenantKeyLoading } = useAdmin();
  const { getUserList, getUsersListLoading } = useUser();
  const { getLocationsAPI, getLocationsLoading } = useLocations();
  const currentClientId = useAppSelector(clientId);

  // Refs
  const formRef = useRef<HTMLFormElement>(null);
  const barcodeTimeoutRef = useRef<NodeJS.Timeout>();

  // Local storage values
  const logInUserName = localStorage.getItem("username") ?? "";
  const logInUserfName = localStorage.getItem("firstName") ?? "";
  const logInUserlName = localStorage.getItem("lastName") ?? "";
  const logInUserId = localStorage.getItem("id") ?? "";

  // State
  const [state, setState] = useState<RecordUsageState>(initialRecordUsageState);
  const [showAddPatientModal, setShowAddPatientModal] = useState(false);
  const [barcodeErrorMsg, setBarcodeErrorMsg] = useState<string | null>(null);
  const [selectedPatientOpt, setSelectedPatientOpt] = useState<any | null>(null);
  const [selectedProviderOpt, setSelectedProviderOpt] = useState<any | null>(null);
  const [selectedLocationOpt, setSelectedLocationOpt] = useState<any | null>(null);

  // Initial payload
  const initialPayload: Payload = useMemo(
    () => ({
      accessKey: "",
      patID: "",
      patFName: "",
      patMI: "",
      patLName: "",
      patDOB: "",
      patGender: "",
      userID: logInUserName,
      userFName: logInUserfName,
      userMI: "",
      userLName: logInUserlName,
      userNum: "",
      hcpid: "",
      hcpfName: "",
      hcpmi: "",
      hcplName: "",
      hcpNum: "",
      serverDate: "",
      serverTime: "",
      location: "",
      barcode: "",
      qty: "1",
      units: "",
      query: "",
      uniqueId: "",
      inventory: true,
      expiringInventory: true,
      emrOrderId: "",
      name: "",
      encounterId: "",
      encounterDate: "",
      emrOrderDate: "",
      reason: "test",
      waste: "0",
      clientId: currentClientId ?? "",
    }),
    [logInUserName, logInUserfName, logInUserlName, currentClientId]
  );

  // Initialize data
  useEffect(() => {
    initializeData();
  }, []);

  const initializeData = async () => {
    try {
      // Get tenant key
      const tenantRes = await getTenantKeyAPI({});
      recordUsageForm.setFieldValue(
        "accessKey",
        tenantRes[0]?.settingValue ?? ""
      );

      // Get dropdown values
      const ddRes = await getDDvaluesAPI({});

      setState((prev) => ({
        ...prev,
        ddLocation: ddRes.locations.map((item: any) => ({
          value: `${item.locationName} - ${item.locationCode}`,
          label: `${item.locationName} - ${item.locationCode}`,
        })),
        locationItem: ddRes.locations.map((item: any) => ({
          ...item,
          fullName: `${item.locationName} - ${item.locationCode}`,
        })),
        patients: ddRes.patients.map((item: any) => ({
          ...item,
          fullName: `${item.lastName}, ${item.firstName} ${item.mi ?? ""} (${item.patientCode} ${item.dob ? moment(item.dob).format("MM/DD/YYYY") : ""})`,
        })),
        ddPatients: ddRes.patients.map((item: any) => ({
          value: item.id,
          label: `${item.lastName}, ${item.firstName} ${item.mi ?? ""} (${item.patientCode} ${item.dob ? moment(item.dob).format("MM/DD/YYYY") : ""})`,
        })),
        providers: ddRes.providers.map((item: any) => ({
          ...item,
          fullName: `${item.lName}, ${item.fName} (${item.userName})`,
        })),
        ddProviders: ddRes.providers.map((item: any) => ({
          value: item.id,
          label: `${item.lName}, ${item.fName} (${item.userName})`,
        })),
        recordUsagePageSetting: ddRes.recordUsagePageSetting,
      }));
    } catch (error) {
      console.error("Failed to initialize data:", error);
      toast.error("Failed to load initial data");
    }
  };

  // Main form validation
  const recordUsageForm = useFormik({
    initialValues: initialPayload,
    validationSchema: recordUsageValidationSchema,
    onSubmit: async (values) => {
      await handleRecordUsage(values);
    },
  });

  // Clear item data when barcode is cleared
  useEffect(() => {
    if (!recordUsageForm.values.barcode) {
      setState((prev) => ({ ...prev, itemData: undefined }));
    }
  }, [recordUsageForm.values.barcode]);

  // Patient modal form validation
  const patientForm = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      dob: null as Date | null,
      gender: "Male",
      patientCode: "",
      isActive: true,
    },
    validationSchema: patientValidationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const patientRes = await createPatientAPI({
          ...values,
          dob: values.dob ? values.dob.toISOString().split("T")[0] : "",
        });

        if (patientRes?.id) {
          getPatientOptions(patientRes?.patientCode, true);
          setShowAddPatientModal(false);
          resetForm();
          toast.success("Patient added successfully");
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to add patient");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const getPatientOptions = useCallback(
    async (searchText: string = "", isOptSelect?: boolean) => {
      try {
        const res: any = await getPatientAPI({ searchText });
        if (res && _.isArray(res) && res.length > 0) {
          const patients = res.map((item: any) => ({
            value: item,
            label: `${item.lastName}, ${item.firstName} ${item.mi ?? ""} (${item.patientCode})`,
          }));
          if (isOptSelect && patients.length) {
            const newOpt = patients[0];
            handlePatientSelect(newOpt);
          }
          return patients;
        } else {
          return [];
        }
      } catch (err: any) {
        console.error(err);
        return [];
      }
    },
    [getPatientAPI]
  );

  const getProviderOptions = useCallback(
    async (searchText: string = "") => {
      try {
        const res: any = await getUserList({ searchText });
        if (res && _.isArray(res) && res.length > 0) {
          const providers = res.filter(
            (item) => item?.clinicalRole?.roleName === "PROV"
          );
          return providers?.map((item: any) => ({
            value: item,
            label: `${item.lName}, ${item.fName} (${item.userName})`,
          }));
        } else {
          return [];
        }
      } catch (err: any) {
        console.error(err);
        return [];
      }
    },
    [getUserList]
  );

  const getLocationOptions = useCallback(
    async (searchText: string = "") => {
      try {
        const res: any = await getLocationsAPI({ searchText });
        if (res && _.isArray(res) && res.length > 0) {
          return res.map((item: any) => ({
            value: item,
            label: `${item.locationName} - ${item.locationCode}`,
          }));
        } else {
          return [];
        }
      } catch (err: any) {
        console.error(err);
        return [];
      }
    },
    [getLocationsAPI]
  );

  const handlePatientSelect = useCallback(
    (option: any) => {
      const patient = option?.value || option;
      setSelectedPatientOpt(option || null);
      recordUsageForm.setValues((prev) => ({
        ...prev,
        patID: patient?.patientCode ?? "",
        patFName: patient?.firstName ?? "",
        patMI: patient?.mi ?? "",
        patLName: patient?.lastName ?? "",
        patDOB: patient?.dob ?? "",
        patGender: patient?.gender ?? "",
      }));
    },
    [recordUsageForm]
  );

  const handleProviderSelect = useCallback(
    (option: any) => {
      const provider = option?.value || option;
      setSelectedProviderOpt(option || null);
      recordUsageForm.setValues((prev) => ({
        ...prev,
        hcpid: provider?.userName ?? "",
        hcpfName: provider?.fName ?? "",
        hcpmi: provider?.mi ?? "",
        hcplName: provider?.lName ?? "",
        hcpNum: provider?.deaNumber ?? "",
      }));
    },
    [recordUsageForm]
  );

  const handleLocationSelect = useCallback(
    (option: any) => {
      const location = option?.value || option;
      setSelectedLocationOpt(option || null);
      recordUsageForm.setFieldValue(
        "location",
        location ? `${location.locationCode}:${location.locationName}` : ""
      );
    },
    [recordUsageForm]
  );

  const handleBarcodeBlur = useCallback(async () => {
    const barcode = recordUsageForm.values.barcode;
    if (!barcode) return;

    // Clear previous timeout
    if (barcodeTimeoutRef.current) {
      clearTimeout(barcodeTimeoutRef.current);
    }

    // Debounce barcode API call
    barcodeTimeoutRef.current = setTimeout(async () => {
      try {
        const res: RecordUsageRes = await getBarcodeDetailsAPI({
          ...recordUsageForm.values,
          query: "true",
        });

        if (res.status === "F") {
          toast.error(res.msg);
          setBarcodeErrorMsg(res.msg);
          return;
        }

        setBarcodeErrorMsg(null);

        /* Here in API response we get unitName as unitCode, thats why using unitName for both value and label */
        const updatedUseUnitsList =
          res?.useUnits?.map(({ unitName, isDefault }) => ({
            value: unitName,
            label: unitName,
            isDefault: !!isDefault,
          })) ?? [];

        const defaultUnit =
          updatedUseUnitsList.find((u) => u.isDefault) ||
          updatedUseUnitsList[0] ||
          null;

        recordUsageForm.setFieldValue("units", defaultUnit?.value || "");

        setState((prev) => ({
          ...prev,
          itemData: res,
          useUnitsList: updatedUseUnitsList,
        }));

        toast.success(res.msg);
      } catch (error) {
        console.error("Barcode lookup failed:", error);
        toast.error("Failed to lookup barcode");
      }
    }, 500);
  }, [recordUsageForm.values, getBarcodeDetailsAPI]);

  const handleRecordUsage = async (values: Payload) => {
    try {
      const res = await getBarcodeDetailsAPI({ ...values, query: "false" });

      if (res.status === "F") {
        toast.error(res.msg);
        setBarcodeErrorMsg(res.msg);
        return;
      }

      setBarcodeErrorMsg(null);
      toast.success(res.msg);

      // Handle waste if applicable
      if (parseFloat(values.waste) > 0) {
        await getBarcodeDetailsAPI({
          ...values,
          qty: values.waste,
          query: "WASTEINV",
        });
      }

      // Add to shot data
      setState((prev) => ({
        ...prev,
        shotData: [
          ...prev.shotData,
          {
            ...res,
            stock: state.itemData?.list2,
            qty: values.qty,
            unit: values.units,
            barcode: values.barcode,
            waste: values.waste,
          },
        ],
      }));

      // Reset form fields except patient, provider, location
      const updatedValues = {
        barcode: initialPayload.barcode,
        qty: initialPayload.qty,
        units: initialPayload.units,
        waste: initialPayload.waste,
      };
      Object.entries(updatedValues).forEach(([key, val]) => {
        recordUsageForm.setFieldValue(key as any, val);
      });
    } catch (error) {
      console.error("Record usage failed:", error);
      toast.error("Failed to record usage");
    }
  };

  const setCurrentUser = useCallback(() => {
    const currentUser = {
      id: logInUserId,
      fName: logInUserfName,
      lName: logInUserlName,
      fullName: logInUserName,
      userName: logInUserName,
      mi: "",
    };

    const option = {
      value: currentUser,
      label: `${currentUser.lName}, ${currentUser.fName} (${currentUser.userName})`,
    };

    if (state.recordUsagePageSetting?.isProviderControlFreeText) {
      handleProviderSelect(option);
    } else {
      const providerOption = {
        value: currentUser.id,
        label: `${currentUser.lName}, ${currentUser.fName} (${currentUser.userName})`,
      };

      setState((prev) => {
        const isProviderInList = prev.ddProviders.some(
          (p: any) => p.value === currentUser.id
        );
        if (isProviderInList) {
          return prev;
        }
        return {
          ...prev,
          ddProviders: [...prev.ddProviders, providerOption],
        };
      });
      handleProviderSelect(option);
    }
  }, [
    logInUserId,
    logInUserfName,
    logInUserlName,
    logInUserName,
    state.recordUsagePageSetting,
  ]);

  const clearData = useCallback(() => {
    // Reset form fields
    recordUsageForm.setValues(initialPayload);
    recordUsageForm.setTouched({});

    // Reset relevant state while preserving shotData
    setState((prev) => ({
      ...prev,
      itemData: undefined,
      useUnitsList: [],
      isCurrentUserSet: false,
      locationItem: prev.locationItem,
      recordUsagePageSetting: prev.recordUsagePageSetting,
      patients: [],
      ddPatients: [],
      ddProviders: prev.ddProviders,
      ddLocation: prev.ddLocation,
      providers: prev.providers,
      shotData: prev.shotData,
    }));

    setBarcodeErrorMsg(null);
    setSelectedPatientOpt(null);
    setSelectedProviderOpt(null);
    setSelectedLocationOpt(null);
    initializeData();
  }, [initialPayload, recordUsageForm]);

  const toggleShowAddPatientModal = useCallback(() => {
    toggleClass();
    setShowAddPatientModal((prev) => !prev);
    if (showAddPatientModal) {
      patientForm.resetForm();
    }
  }, [showAddPatientModal, patientForm]);

  // Retrive location wise quantity data
  const getLocationWiseData = useMemo(() => {
    const { location } = recordUsageForm.values;
    const { inventory = [], uom = "" } = state.itemData || {};
    const selectedCode = selectedLocationOpt?.locationCode;
    const selectedName = selectedLocationOpt?.locationName;

    if (!location || !inventory.length) return "Inventory";

    const item = inventory.find((inv: any) => inv.locationID === selectedCode);
    const qty = item?.netQty ?? 0;
    const locName = item?.locationName ?? selectedName ?? "";

    return `(${qty} ${uom} - ${locName})`;
  }, [recordUsageForm.values.location, state.itemData, selectedLocationOpt]);

  const quantityPopover = useMemo(() => {
    const inventory = state.itemData?.inventory ?? [];
    const uom = state.itemData?.uom ?? "";

    return (
      <Popover id="quantityPopover">
        <Popover.Body>
          <Table hover responsive>
            <thead>
              <tr>
                <th>Quantity</th>
                <th>Unit</th>
                <th>Location</th>
              </tr>
            </thead>
            <tbody>
              {inventory.length > 0 ? (
                inventory.map((item: any, index: number) => (
                  <tr key={index}>
                    <td>{item?.netQty}</td>
                    <td>{uom}</td>
                    <td>{item?.locationFullName}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} style={{ textAlign: "center", color: "red" }}>
                    Item not found.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Popover.Body>
      </Popover>
    );
  }, [state.itemData]);

  const loadPatientOptions = useMemo(() => {
    const debouncedFn = _.debounce(
      (inputValue: string, callback: (options: any[]) => void) => {
        getPatientOptions(inputValue).then(callback);
      },
      300
    );

    return (inputValue: string) =>
      new Promise<any[]>((resolve) => {
        debouncedFn(inputValue, resolve);
      });
  }, [getPatientOptions]);

  const loadProviderOptions = useMemo(() => {
    const debouncedFn = _.debounce(
      (inputValue: string, callback: (options: any[]) => void) => {
        getProviderOptions(inputValue).then(callback);
      },
      300
    );

    return (inputValue: string) =>
      new Promise<any[]>((resolve) => {
        debouncedFn(inputValue, resolve);
      });
  }, [getProviderOptions]);

  const loadLocationOptions = useMemo(() => {
    const debouncedFn = _.debounce(
      (inputValue: string, callback: (options: any[]) => void) => {
        getLocationOptions(inputValue).then(callback);
      },
      300
    );

    return (inputValue: string) =>
      new Promise<any[]>((resolve) => {
        debouncedFn(inputValue, resolve);
      });
  }, [getLocationOptions]);

  const defaultPatientOptions = useMemo(() => {
    return selectedPatientOpt ? [selectedPatientOpt] : [];
  }, [selectedPatientOpt]);

  const defaultProviderOptions = useMemo(() => {
    return selectedProviderOpt ? [selectedProviderOpt] : [];
  }, [selectedProviderOpt]);

  const defaultLocationOptions = useMemo(() => {
    return selectedLocationOpt ? [selectedLocationOpt] : [];
  }, [selectedLocationOpt]);

  const isLoading = getDDvaluesLoading || getBarcodeDetailsLoading || getTenantKeyLoading || createPatientLoading || getPatientLoading || getUsersListLoading || getLocationsLoading;

  return (
    <>
      <div className="page-title">
        {isLoading && <Loading />}
        <h2>Record Usage</h2>
      </div>

      <div className="record-usage-item-box">
        <Form ref={formRef} onSubmit={recordUsageForm.handleSubmit}>
          <h5 className="form-section-title full-width">Basic Information</h5>

          {/* Patient Field */}
          <Form.Group className="mb-3">
            <Form.Label>
              Patient ID*
              {checkPermission(
                PermissionModule.Add_PATIENT_ON_RECORD_USAGE,
                PermissionIdentifier.ADD_PATIENT_ON_RECORD_USAGE
              ) && (
                  <Badge onClick={toggleShowAddPatientModal} bg="primary">
                    Add Patient
                  </Badge>
                )}
            </Form.Label>

            {state.recordUsagePageSetting?.isPatientControlFreeText ? (
              <AsyncSelect
                cacheOptions={false}
                value={selectedPatientOpt}
                key={selectedPatientOpt?.value?.id}
                isLoading={getPatientLoading}
                classNamePrefix="react-select"
                placeholder="Enter Patient ID"
                loadOptions={loadPatientOptions}
                onChange={handlePatientSelect}
                defaultOptions={defaultPatientOptions}
                components={{ DropdownIndicator: () => null }}
                className={`${recordUsageForm.touched.patID && recordUsageForm.errors.patID ? "is-invalid-border" : ""} react-select-container`}
              />
            ) : (
              <Select
                classNamePrefix="react-select"
                options={state.ddPatients}
                value={
                  recordUsageForm.values.patID
                    ? state.ddPatients?.find(
                      (option: DropDownValues) =>
                        option.value === recordUsageForm.values.patID
                    )
                    : ""
                }
                onChange={(option: any) =>
                  handlePatientSelect(
                    state.patients.find((item: any) => item.id === option.value)
                  )
                }
                className={`${recordUsageForm.touched.patID && recordUsageForm.errors.patID ? "is-invalid-border" : ""} react-select-container`}
                placeholder="Enter Patient"
              />
            )}

            {recordUsageForm.touched.patID && recordUsageForm.errors.patID && (
              <Form.Control.Feedback type="invalid">
                {recordUsageForm.errors.patID}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          {/* Provider Field */}
          <Form.Group className="mb-3">
            <Form.Label>Provider*</Form.Label>
            <div className="input-with-button">
              {state.recordUsagePageSetting?.isProviderControlFreeText ? (
                <AsyncSelect
                  cacheOptions={false}
                  value={selectedProviderOpt}
                  isLoading={getUsersListLoading}
                  classNamePrefix="react-select"
                  placeholder="Enter Provider"
                  loadOptions={loadProviderOptions}
                  onChange={handleProviderSelect}
                  defaultOptions={defaultProviderOptions}
                  components={{ DropdownIndicator: () => null }}
                  className={`${recordUsageForm.touched.hcpid && recordUsageForm.errors.hcpid ? "is-invalid-border" : ""} react-select-container w-100`}
                />
              ) : (
                <Select
                  classNamePrefix="react-select"
                  options={state.ddProviders}
                  value={
                    recordUsageForm.values.hcpid
                      ? state.ddProviders?.find(
                        (option: DropDownValues) =>
                          option.value === recordUsageForm.values.hcpid
                      )
                      : ""
                  }
                  onChange={(option: any) =>
                    handleProviderSelect(
                      state.providers.find(
                        (item: any) => item.id === option.value
                      )
                    )
                  }
                  className={`${recordUsageForm.touched.hcpid && recordUsageForm.errors.hcpid ? "is-invalid-border" : ""} react-select-container w-100`}
                  placeholder="Enter Provider"
                />
              )}

              {state.recordUsagePageSetting?.showCurrentUserButton && (
                <OverlayTrigger
                  placement="top"
                  delay={{ show: 250, hide: 400 }}
                  overlay={<Tooltip>Apply Current User</Tooltip>}
                  trigger={["hover", "focus"]}
                >
                  <button type="button" onClick={setCurrentUser}>
                    <i className="bi bi-chevron-left"></i>
                    <span>User</span>
                  </button>
                </OverlayTrigger>
              )}
            </div>

            {recordUsageForm.touched.hcpid && recordUsageForm.errors.hcpid && (
              <Form.Control.Feedback type="invalid">
                {recordUsageForm.errors.hcpid}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          {/* Location Field */}
          <Form.Group className="mb-3">
            <Form.Label>Location*</Form.Label>

            {state.recordUsagePageSetting?.isLocationControlFreeText ? (
              <AsyncSelect
                cacheOptions={false}
                value={selectedLocationOpt}
                isLoading={getLocationsLoading}
                classNamePrefix="react-select"
                placeholder="Enter Location"
                loadOptions={loadLocationOptions}
                onChange={handleLocationSelect}
                defaultOptions={defaultLocationOptions}
                components={{ DropdownIndicator: () => null }}
                className={`${recordUsageForm.touched.location && recordUsageForm.errors.location ? "is-invalid-border" : ""} react-select-container`}
              />
            ) : (
              <Select
                classNamePrefix="react-select"
                options={state.ddLocation}
                value={
                  recordUsageForm.values.location
                    ? state.ddLocation?.find(
                      (option: DropDownValues) =>
                        option.value === recordUsageForm.values.location
                    )
                    : ""
                }
                onChange={(option: any) =>
                  handleLocationSelect(
                    state.locationItem.find(
                      (item: any) => item.fullName === option.value
                    )
                  )
                }
                className={`${recordUsageForm.touched.location && recordUsageForm.errors.location ? "is-invalid-border" : ""} react-select-container`}
                placeholder="Enter Location"
              />
            )}

            {recordUsageForm.touched.location &&
              recordUsageForm.errors.location && (
                <Form.Control.Feedback type="invalid">
                  {recordUsageForm.errors.location}
                </Form.Control.Feedback>
              )}
          </Form.Group>

          <h5 className="form-section-title full-width">
            Enter Item Usage Details
          </h5>

          {/* Barcode Field */}
          <Form.Group className="mb-3">
            <Form.Label>Barcode*</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Barcode"
              name="barcode"
              value={recordUsageForm.values.barcode}
              onChange={(e) => {
                recordUsageForm.handleChange(e);
                setBarcodeErrorMsg(null);
              }}
              onBlur={handleBarcodeBlur}
              isInvalid={
                (recordUsageForm.touched.barcode &&
                  !!recordUsageForm.errors.barcode) ||
                !!barcodeErrorMsg
              }
            />
            {((recordUsageForm.touched.barcode &&
              recordUsageForm.errors.barcode) ||
              barcodeErrorMsg) && (
                <Form.Control.Feedback type="invalid">
                  {barcodeErrorMsg || recordUsageForm.errors.barcode}
                </Form.Control.Feedback>
              )}
          </Form.Group>

          {/* Quantity Field */}
          <Form.Group className="mb-3">
            <Form.Label>Quantity*</Form.Label>
            <InputGroup>
              <Form.Control
                type="number"
                placeholder="Enter Quantity"
                name="qty"
                value={recordUsageForm.values.qty}
                onChange={recordUsageForm.handleChange}
                onBlur={recordUsageForm.handleBlur}
                isInvalid={
                  recordUsageForm.touched.qty && !!recordUsageForm.errors.qty
                }
              />

              {state.useUnitsList.length > 0 && (
                <Form.Select
                  aria-label="Select Quantity"
                  name="units"
                  value={recordUsageForm.values?.units ?? ""}
                  onChange={recordUsageForm.handleChange}
                  className="unit-select"
                >
                  {state.useUnitsList.map((unit: any, index: number) => (
                    <option key={index} value={unit.value}>
                      {unit.label}
                    </option>
                  ))}
                </Form.Select>
              )}
            </InputGroup>

            {recordUsageForm.touched.qty && recordUsageForm.errors.qty && (
              <Form.Control.Feedback type="invalid">
                {recordUsageForm.errors.qty}
              </Form.Control.Feedback>
            )}

            <OverlayTrigger
              trigger="hover"
              placement="bottom"
              overlay={quantityPopover}
            >
              <Form.Text id="quantity-hint" muted>
                {state.itemData && <span>{getLocationWiseData}</span>}
              </Form.Text>
            </OverlayTrigger>
          </Form.Group>

          {/* Waste Field */}
          <Form.Group className="mb-3">
            <Form.Label>Waste</Form.Label>
            <Form.Control
              type="number"
              placeholder="Enter Waste"
              name="waste"
              value={recordUsageForm.values.waste}
              onChange={recordUsageForm.handleChange}
              onBlur={recordUsageForm.handleBlur}
              isInvalid={
                recordUsageForm.touched.waste && !!recordUsageForm.errors.waste
              }
            />
            {recordUsageForm.touched.waste && recordUsageForm.errors.waste && (
              <Form.Control.Feedback type="invalid">
                {recordUsageForm.errors.waste}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          <div className="record-item-info-wrapper full-width">
            {state.itemData && (
              <>
                <h5 className="form-section-title full-width">Item Details</h5>
                <div className="record-item-info-container">
                  <h5 className="form-section-title full-width">
                    {state.itemData?.rxName ?? "-"}
                  </h5>
                  <div className="record-item-info-box">
                    <label>Item Group</label>
                    <p>{state.itemData?.rxCategory ?? "-"}</p>
                  </div>
                  <div className="record-item-info-box">
                    <label>NDC11</label>
                    <p>{state.itemData?.ndC11 ?? "-"}</p>
                  </div>
                  <div className="record-item-info-box">
                    <label>Manufacturer</label>
                    <p>{state.itemData?.manufacturer ?? "-"}</p>
                  </div>
                  <div className="record-item-info-box">
                    <label>Serial Number</label>
                    <p>NA</p>
                  </div>
                  <div className="record-item-info-box">
                    <label>NDC/SKU</label>
                    <p>{state.itemData?.ndc ?? "-"}</p>
                  </div>
                  <div className="record-item-info-box">
                    <label>User Def. 2</label>
                    <p>
                      {state.itemData?.inventory
                        ? state.itemData.inventory[0]?.item?.userDef1
                        : "-"}
                    </p>
                  </div>
                  <div className="record-item-info-box">
                    <label>LOT Number</label>
                    <p>{state.itemData?.lotNo ?? "-"}</p>
                  </div>
                  <div className="record-item-info-box">
                    <label>Cost ($)</label>
                    <p>{state.itemData?.text3 ?? "-"}</p>
                  </div>
                  <div className="record-item-info-box">
                    <label>Expiration Date</label>
                    <p>{state.itemData?.expDate ?? "-"}</p>
                  </div>
                  <div className="record-item-info-box">
                    <label>Stock</label>
                    <p>{state.itemData?.list2 ?? "-"}</p>
                  </div>
                  <div className="record-item-info-box">
                    <label>CVX/Model</label>
                    <p>{state.itemData?.cvx ?? "-"}</p>
                  </div>
                  <div className="record-item-info-box">
                    <label>Item Type</label>
                    <p>{state.itemData?.list1 ?? "-"}</p>
                  </div>
                </div>
              </>
            )}

            {state.shotData.length > 0 && (
              <div className="record-item-info-table-wrapper">
                <Table bordered={false} hover={true}>
                  <thead>
                    <tr>
                      <th>Barcode</th>
                      <th>Item Name</th>
                      <th>NDC/SKU</th>
                      <th>Lot</th>
                      <th>Exp.Date</th>
                      <th>Stock</th>
                      <th>Quantity</th>
                      <th>Unit</th>
                      <th>Waste</th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.shotData.map((data: any, index: number) => (
                      <tr key={index}>
                        <td>{data.barcode ?? "-"}</td>
                        <td>{data.rxName ?? "-"}</td>
                        <td>{data.ndc ?? "-"}</td>
                        <td>{data.lotNo ?? "-"}</td>
                        <td>{data.expDate ?? "-"}</td>
                        <td>{data.stock ?? "-"}</td>
                        <td>{data.qty ?? "-"}</td>
                        <td>{data.unit ?? "-"}</td>
                        <td>{data.waste ?? "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </div>

          <Form.Group className="mb-3 mt-4 full-width text-end">
            <Button
              type="button"
              style={{ marginRight: "10px" }}
              variant="outline-primary"
              onClick={clearData}
            >
              Cancel
            </Button>
            <Button type="submit">Record</Button>
          </Form.Group>
        </Form>
      </div>

      {showAddPatientModal && (
        <CustomModal
          id="addpatientcmodal"
          title="Add Patient"
          content={
            <>
              <Form noValidate onSubmit={patientForm.handleSubmit}>
                <div className="add-patient-box">
                  <Form.Group className="mb-2">
                    <Form.Label>
                      Patient ID<sup>*</sup>
                    </Form.Label>
                    <Form.Control
                      id="patientCode"
                      name="patientCode"
                      type="text"
                      placeholder="Enter Patient ID"
                      onChange={patientForm.handleChange}
                      value={patientForm.values.patientCode}
                      isInvalid={
                        patientForm.touched.patientCode &&
                        !!patientForm.errors.patientCode
                      }
                    />
                    {patientForm.touched.patientCode &&
                      patientForm.errors.patientCode ? (
                      <Form.Control.Feedback type="invalid">
                        {patientForm.errors.patientCode}
                      </Form.Control.Feedback>
                    ) : null}
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label>
                      First Name<sup>*</sup>
                    </Form.Label>
                    <Form.Control
                      id="firstName"
                      name="firstName"
                      type="text"
                      placeholder="Enter First Name"
                      onChange={patientForm.handleChange}
                      value={patientForm.values.firstName}
                      isInvalid={
                        patientForm.touched.firstName &&
                        !!patientForm.errors.firstName
                      }
                    />
                    {patientForm.touched.firstName &&
                      patientForm.errors.firstName ? (
                      <Form.Control.Feedback type="invalid">
                        {patientForm.errors.firstName}
                      </Form.Control.Feedback>
                    ) : null}
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label>
                      Last Name<sup>*</sup>
                    </Form.Label>
                    <Form.Control
                      id="lastName"
                      name="lastName"
                      type="text"
                      placeholder="Enter Last Name"
                      onChange={patientForm.handleChange}
                      value={patientForm.values.lastName}
                      isInvalid={
                        patientForm.touched.lastName &&
                        !!patientForm.errors.lastName
                      }
                    />
                    {patientForm.touched.lastName &&
                      patientForm.errors.lastName ? (
                      <Form.Control.Feedback type="invalid">
                        {patientForm.errors.lastName}
                      </Form.Control.Feedback>
                    ) : null}
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label>DOB</Form.Label>
                    <div className="calender-box">
                      <DatePicker
                        locale="en-US"
                        format="MM/dd/yyyy"
                        className={`${patientForm.touched.dob && !!patientForm.errors.dob ? "is-invalid-border" : ""} form-control`}
                        openCalendarOnFocus={false}
                        dayPlaceholder="dd"
                        monthPlaceholder="mm"
                        yearPlaceholder="yyyy"
                        showLeadingZeros={true}
                        value={patientForm.values.dob}
                        clearIcon={null}
                        calendarIcon={<i className="bi bi-calendar"></i>}
                        onChange={(date) =>
                          patientForm.setFieldValue("dob", date)
                        }
                      />
                    </div>
                    {patientForm.touched.dob && patientForm.errors.dob ? (
                      <div className="invalid-feedback d-block">
                        {patientForm.errors.dob}
                      </div>
                    ) : null}
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label>
                      Gender<sup>*</sup>
                    </Form.Label>
                    <div className="item-radio-content">
                      {["Male", "Female", "Other"].map((gender) => (
                        <Form.Check
                          key={gender}
                          inline
                          type="radio"
                          id={`gender-${gender}`}
                          name="gender"
                          label={gender}
                          value={gender}
                          onChange={patientForm.handleChange}
                          checked={patientForm.values.gender === gender}
                        />
                      ))}
                      {patientForm.touched.gender &&
                        patientForm.errors.gender ? (
                        <div className="invalid-feedback d-block">
                          {patientForm.errors.gender}
                        </div>
                      ) : null}
                    </div>
                  </Form.Group>

                  <div className="full-width  text-end">
                    <Button
                      type="button"
                      style={{ marginRight: "10px" }}
                      variant="outline-primary"
                      onClick={toggleShowAddPatientModal}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={false}>
                      Add
                    </Button>
                  </div>
                </div>
              </Form>
            </>
          }
          onClose={toggleShowAddPatientModal}
        />
      )}
    </>
  );
};

export default RecordUsage;
